import { supabase } from './supabase/client';
import { logger } from '@deskbay/shared';

export interface NotificationTranslation {
    title: string;
    subtitle?: string;
    message: string;
    action_label?: string;
}

export interface SystemNotification {
    id: string;
    target_type: 'all' | 'individual';
    target_restaurant_ids: string[] | null;
    type: string; // 'announcement' | 'maintenance' | 'custom' | 'system_alert' | 'booking_notification'
    priority: 'urgent' | 'normal' | 'low';
    category?: 'system' | 'bookings' | 'config';
    title: string;
    message: string;
    is_read: boolean;
    is_dismissed: boolean;
    action_label?: string;
    action_url?: string;
    target_filter?: { translations?: Record<string, NotificationTranslation>; [key: string]: unknown };
    created_at: string;
    status?: string;
    channel?: string;
}

/**
 * Get system notifications for the current user
 * Uses RLS policies to scope notifications to restaurants the user owns/is member of,
 * then filters client-side to the currently selected restaurant (multi-restaurant users
 * would otherwise see individual-targeted notifications, e.g. bookings, from ALL their
 * restaurants instead of just the active one).
 * @param restaurantId - Currently selected restaurant; used both as a load gate and as
 * the client-side filter for target_type === 'individual' notifications.
 */
export const getSystemNotifications = async (restaurantId?: string): Promise<SystemNotification[]> => {
    if (!restaurantId) return [];

    // Get current user from session (getSession = local read, avoids AuthSessionMissingError on native)
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return [];

    // Filter: only show notifications created AFTER user registration date
    // This ensures new users don't see old notifications from before they signed up
    const userCreatedAt = user.created_at;

    // Query with LEFT JOIN to get user state
    // RLS policies on system_notifications will filter based on target_type and restaurant membership
    const { data, error } = await supabase
        .from('system_notifications')
        .select(`
            *,
            notification_user_states!left (
                is_read,
                is_dismissed,
                read_at,
                dismissed_at
            )
        `)
        .eq('status', 'sent')
        .gte('created_at', userCreatedAt)
        .eq('notification_user_states.user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(500); // Cap fetch iniziale (mirror pattern bookings, restaurants.ts:152-164).
                     // Reveal incrementale è client-side (NotificationCenterPage).

    if (error) {
        logger.notifications.error('Error fetching notifications', error, 'notificationService');
        return [];
    }

    // Transform data to flatten the user state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase joined query result with dynamic shape
    const notifications = (data || []).map((notif: any) => {
        // Get user state from joined table (will be null if user hasn't interacted with notification)
        const userState = notif.notification_user_states?.[0] || null;

        return {
            id: notif.id,
            target_type: notif.target_type,
            target_restaurant_ids: notif.target_restaurant_ids,
            type: notif.type,
            priority: notif.priority,
            category: notif.category,
            title: notif.title,
            message: notif.message,
            is_read: userState?.is_read || false,
            is_dismissed: userState?.is_dismissed || false,
            action_label: notif.action_label,
            action_url: notif.action_url,
            created_at: notif.created_at,
            status: notif.status,
            channel: notif.channel,
        };
    });

    // Restrict individual-targeted notifications (e.g. bookings) to the active restaurant.
    // 'all'-targeted notifications (platform announcements) stay visible regardless.
    const scoped = notifications.filter(notif =>
        notif.target_type !== 'individual' ||
        notif.target_restaurant_ids?.includes(restaurantId)
    );

    return scoped as SystemNotification[];
};

/**
 * Get dismissed (archived) notifications for the current user.
 * Separate from getSystemNotifications: the archive tab is historical, not "live"
 * (no realtime subscription), and intentionally uses a higher cap since it's meant
 * to hold the long-term history rather than just the recent active window.
 * Queried from notification_user_states (not system_notifications) so `is_dismissed`
 * can be filtered directly, without LEFT JOIN ambiguity.
 */
export const getArchivedNotifications = async (
    restaurantId?: string,
    limit = 2000
): Promise<SystemNotification[]> => {
    if (!restaurantId) return [];

    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return [];

    const { data, error } = await supabase
        .from('notification_user_states')
        .select(`
            is_read,
            is_dismissed,
            read_at,
            dismissed_at,
            system_notifications!inner (*)
        `)
        .eq('user_id', user.id)
        .eq('is_dismissed', true)
        .eq('system_notifications.status', 'sent')
        .order('created_at', { referencedTable: 'system_notifications', ascending: false })
        .limit(limit);

    if (error) {
        logger.notifications.error('Error fetching archived notifications', error, 'notificationService');
        return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase joined query result with dynamic shape
    const notifications = (data || []).map((row: any) => {
        const notif = row.system_notifications;
        return {
            id: notif.id,
            target_type: notif.target_type,
            target_restaurant_ids: notif.target_restaurant_ids,
            type: notif.type,
            priority: notif.priority,
            category: notif.category,
            title: notif.title,
            message: notif.message,
            is_read: row.is_read || false,
            is_dismissed: row.is_dismissed || false,
            action_label: notif.action_label,
            action_url: notif.action_url,
            target_filter: notif.target_filter,
            created_at: notif.created_at,
            status: notif.status,
            channel: notif.channel,
        };
    });

    const scoped = notifications.filter(notif =>
        notif.target_type !== 'individual' ||
        notif.target_restaurant_ids?.includes(restaurantId)
    );

    return scoped as SystemNotification[];
};

/**
 * Mark a notification as read for the current user
 * Creates or updates a row in notification_user_states
 */
export const markAsRead = async (notificationId: string): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;

    // Upsert: insert if not exists, update if exists
    const { error } = await supabase
        .from('notification_user_states')
        .upsert(
            {
                notification_id: notificationId,
                user_id: user.id,
                is_read: true,
            },
            {
                onConflict: 'notification_id,user_id',
            }
        );

    if (error) {
        logger.notifications.error('Error marking notification as read', error, 'notificationService');
    }
};

/**
 * Mark all visible notifications as read for the current user
 */
export const markAllAsRead = async (restaurantId?: string): Promise<void> => {
    if (!restaurantId) return;

    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;

    // Get all visible notifications (RLS will filter)
    const notifications = await getSystemNotifications(restaurantId);
    const unreadNotifications = notifications.filter(n => !n.is_read);

    if (unreadNotifications.length === 0) return;

    // Upsert all unread notifications as read
    const upsertData = unreadNotifications.map(notif => ({
        notification_id: notif.id,
        user_id: user.id,
        is_read: true,
    }));

    const { error } = await supabase
        .from('notification_user_states')
        .upsert(upsertData, {
            onConflict: 'notification_id,user_id',
        });

    if (error) {
        logger.notifications.error('Error marking all notifications as read', error, 'notificationService');
        throw error;
    }
};

/**
 * Mark multiple notifications as read AND dismissed in a single upsert.
 * Used by "mark all as read" in the notification center to avoid one
 * network round-trip per notification.
 */
export const markManyAsReadAndDismissed = async (notificationIds: string[]): Promise<void> => {
    if (notificationIds.length === 0) return;

    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;

    const upsertData = notificationIds.map(id => ({
        notification_id: id,
        user_id: user.id,
        is_read: true,
        is_dismissed: true,
    }));

    const { error } = await supabase
        .from('notification_user_states')
        .upsert(upsertData, {
            onConflict: 'notification_id,user_id',
        });

    if (error) {
        logger.notifications.error('Error marking notifications as read and dismissed', error, 'notificationService');
        throw error;
    }
};

/**
 * Dismiss (archive) a notification for the current user
 * Creates or updates a row in notification_user_states
 */
export const dismissNotification = async (notificationId: string): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;

    // Upsert: insert if not exists, update if exists
    const { error } = await supabase
        .from('notification_user_states')
        .upsert(
            {
                notification_id: notificationId,
                user_id: user.id,
                is_dismissed: true,
            },
            {
                onConflict: 'notification_id,user_id',
            }
        );

    if (error) {
        logger.notifications.error('Error dismissing notification', error, 'notificationService');
    }
};

/**
 * Restore a dismissed notification for the current user
 */
export const restoreNotification = async (notificationId: string): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return;

    // Update existing state to mark as not dismissed
    const { error } = await supabase
        .from('notification_user_states')
        .update({ is_dismissed: false })
        .eq('notification_id', notificationId)
        .eq('user_id', user.id);

    if (error) {
        logger.notifications.error('Error restoring notification', error, 'notificationService');
    }
};
